import React, { memo, useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { IconBackgroundColors, IconColors, IconSizes, IconVariants, IDLE_SCHEDULE } from '../../constants';
import { actions as callActions } from '../../../call';
import ButtonIcon from '../ButtonIcon';
import { isDoctor, isPatient } from '../../../call/helpers';
import { useIdle } from '../../hooks';
import CameraOnIcon from '../../assets/icons/camera-on.svg';
import CameraOffIcon from '../../assets/icons/camera-off.svg';
import MicOnIcon from '../../assets/icons/mic-on.svg';
import MicOffIcon from '../../assets/icons/mic-off.svg';
import CallEndIcon from '../../assets/icons/phone.svg';
import PauseIcon from '../../assets/icons/pause.svg';
import './CallActions.scss';
import { AngularServices, getAngularService } from '../../../../modules/core';
import { CallMenuButton } from '../../../call/components';
import * as localMedia from '../../../local-media';
const CallActionsComponent = ({ hasCameraAccess, hasMicrophoneAccess, hasAudio, hasVideo }) => {
    const [isCallMenuOpen, setIsCallMenuOpen] = useState(false);
    const dispatch = useDispatch();
    const [mutedBy, setMutedBy] = useState(null);
    const holdCallHandle = useCallback(() => dispatch(callActions.holdCall()), []);
    const toggleVideoHandle = useCallback(() => dispatch(localMedia.actions.toggleVideo()), []);
    const toggleAudioHandle = useCallback(() => {
        dispatch(localMedia.actions.toggleAudio());
        setMutedBy(null);
    }, []);
    const endCallHandle = useCallback(() => dispatch(callActions.setEndCallAttempt()), []);
    const getButtonBackground = (isActive) => isActive ? IconBackgroundColors.Primary : IconBackgroundColors.Secondary;
    const isVideoEnabled = hasCameraAccess && hasVideo;
    const isAudioEnabled = hasMicrophoneAccess && hasAudio;
    const hideMutedMessage = (e) => {
        e.stopPropagation();
        setMutedBy(null);
    };
    const onCallMenuToggle = useCallback((isOpen) => {
        setIsCallMenuOpen(isOpen);
    }, []);
    const { isIdle, setIdleToFalse, setIdleToTrue } = useIdle(IDLE_SCHEDULE);
    useEffect(() => {
        const rootScope = getAngularService(AngularServices.RootScope);
        const unsubscribeOnMuteByPerson = rootScope.$on('onTurnOffAudioByPerson', (_event, person) => {
            setMutedBy(person.state.username);
            setIdleToFalse();
            setIdleToTrue();
        });
        return () => {
            unsubscribeOnMuteByPerson();
        };
    }, [setIdleToFalse, setIdleToTrue]);
    useEffect(() => {
        dispatch(localMedia.actions.setMediaState({ hasVideo, hasAudio }));
    }, []);
    useEffect(() => {
        if (isIdle) {
            setMutedBy(null);
        }
    }, [isIdle]);
    return (<div className={classNames({
            'ml-call-actions': true,
            'ml-call-actions--hidden': !isCallMenuOpen && isIdle
        })}>
      <div className="ml-call-actions__content">
        {isDoctor() ? (<div className='ml-call-actions__item'>
            <ButtonIcon size={IconSizes.Large} variant={IconVariants.Rounded} color={IconColors.Primary} backgroundColor={IconBackgroundColors.Primary} clickHandle={holdCallHandle}>
              <PauseIcon />
            </ButtonIcon>
          </div>) : null}

        <div className="ml-call-actions__item" data-testid="video-toggle-mobile">
          <ButtonIcon size={IconSizes.Large} variant={IconVariants.Rounded} color={IconColors.Primary} backgroundColor={getButtonBackground(isVideoEnabled)} clickHandle={toggleVideoHandle}>
            {isVideoEnabled ? <CameraOnIcon /> : <CameraOffIcon />}
          </ButtonIcon>
        </div>

        <div className="ml-call-actions__item" data-testid="audio-toggle-mobile">
          <ButtonIcon size={IconSizes.Large} variant={IconVariants.Rounded} color={IconColors.Primary} backgroundColor={getButtonBackground(isAudioEnabled)} clickHandle={toggleAudioHandle}>
            {isAudioEnabled ? <MicOnIcon /> : <MicOffIcon />}
            {mutedBy && (<div className="call-confirmation-popup call-confirmation-popup--mute-mic" onClick={hideMutedMessage}>
                <div className="call-confirmation-popup__data">
                  <p className="call-confirmation-popup__text call-confirmation-popup__text--align-left">
                    <FormattedMessage id="account.sidebar.video-controls.notification.turnOffAudioByPerson" values={{ username: mutedBy }}/>
                  </p>
                </div>
              </div>)}
          </ButtonIcon>
        </div>
        <div className='ml-call-actions__item'>
          <CallMenuButton isPatient={isPatient()} hasTooltip={false} onToggle={onCallMenuToggle}/>
        </div>

        <div className='ml-call-actions__item'>
          <ButtonIcon size={IconSizes.Large} variant={IconVariants.Rounded} color={IconColors.Danger} backgroundColor={IconBackgroundColors.Primary} clickHandle={endCallHandle}>
            <CallEndIcon />
          </ButtonIcon>
        </div>
      </div>
    </div>);
};
const CallActions = memo(CallActionsComponent);
export default CallActions;
